import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { RefreshCw, FilePlus2, AlertTriangle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// "In Bibliothek speichern": bewusste Aktion mit zwei Optionen.
// - Bestehende Vorlage aktualisieren (nur wenn Block aus einer Vorlage stammt) → Bestätigung
// - Als neue Form speichern → öffnet FormEditorDialog (Parent via onSaveAsNew)
export default function SaveFormDialog({
  open, block, sourceName, focusTypeDefault, onClose, onSaveAsNew, onUpdated,
}) {
  const [confirmUpdate, setConfirmUpdate] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasSource = !!block?.source_form_id;

  const blockToPayload = () => ({
    name: (block.uebung || "").trim() || sourceName || "Unbenannte Form",
    form_type: block.form_type || focusTypeDefault || "spielform",
    focus_area_ids: block.focus_area_ids || [],
    dauer_min: block.dauer_min ?? null,
    spieler_von: block.spieler_von ?? null,
    spieler_bis: block.spieler_bis ?? null,
    organisation: block.organisation || "",
    material: block.material || "",
    ablauf: block.ablauf || "",
    variationen: block.variationen || "",
    coaching: block.coaching || "",
    tipps: block.tipps || "",
    fotos: (block.fotos || []).slice(0, 1),
  });

  const doUpdate = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/exercise-forms/${block.source_form_id}`, blockToPayload());
      toast.success("Vorlage aktualisiert");
      onUpdated?.(data);
      setConfirmUpdate(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aktualisieren fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const close = () => { setConfirmUpdate(false); onClose?.(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold tracking-tight" data-testid="save-form-title">
            In Bibliothek speichern
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Änderungen bewusst in die Bibliothek übernehmen
          </DialogDescription>
        </DialogHeader>

        {!confirmUpdate ? (
          <div className="space-y-2.5 pt-1">
            {hasSource && (
              <button type="button" onClick={() => setConfirmUpdate(true)}
                className="w-full text-left rounded-lg border border-zinc-800 bg-zinc-900/50 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-4 flex items-start gap-3"
                data-testid="save-form-update-existing">
                <div className="h-9 w-9 rounded-md bg-brand/15 text-brand-lighter flex items-center justify-center shrink-0">
                  <RefreshCw className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-100">Bestehende Vorlage aktualisieren</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Überschreibt {sourceName ? <span className="text-zinc-300">„{sourceName}"</span> : "die Ausgangsform"} mit den aktuellen Inhalten.
                  </div>
                </div>
              </button>
            )}
            <button type="button" onClick={() => onSaveAsNew?.(blockToPayload())}
              className="w-full text-left rounded-lg border border-zinc-800 bg-zinc-900/50 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-4 flex items-start gap-3"
              data-testid="save-form-as-new">
              <div className="h-9 w-9 rounded-md bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0">
                <FilePlus2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100">Als neue Form speichern</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Name, Typ und Schwerpunkte vor dem Speichern anpassen. Die Ausgangsform bleibt unverändert.
                </div>
              </div>
            </button>
          </div>
        ) : (
          <div className="pt-1">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-100">
                Die Vorlage {sourceName ? <span className="font-semibold">„{sourceName}"</span> : ""} wird mit den aktuellen Inhalten dieses Blocks überschrieben. Das lässt sich nicht rückgängig machen.
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmUpdate(false)}
                className="flex-1 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900" data-testid="save-form-update-back">
                <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
              </Button>
              <Button onClick={doUpdate} disabled={saving}
                className="flex-1 bg-brand hover:bg-brand-light text-white font-semibold" data-testid="save-form-update-confirm">
                {saving ? "Speichern…" : "Überschreiben"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
